"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractHandshake = exports.MAX_NONCE = exports.MIN_NONCE = void 0;
const x25519 = __importStar(require("@stablelib/x25519"));
const SHA256 = __importStar(require("@stablelib/sha256"));
const chacha20poly1305_1 = require("@stablelib/chacha20poly1305");
const equals_1 = require("uint8arrays/equals");
const concat_1 = require("uint8arrays/concat");
const uint8arrays_1 = require("uint8arrays");
const utils_1 = require("../utils");
const logger_1 = require("../logger");
exports.MIN_NONCE = 0;
// For performance reasons, the nonce is represented as a JS `number`
// JS `number` can only safely represent integers up to 2 ** 53 - 1
// This is a slight deviation from the noise spec, which describes the max nonce as 2 ** 64 - 2
// The effect is that this implementation will need a new handshake to be performed after fewer messages are exchanged than other implementations with full uint64 nonces.
// 2 ** 53 - 1 is still a large number of messages, so the practical effect of this is negligible.
exports.MAX_NONCE = Number.MAX_SAFE_INTEGER;
const ERR_MAX_NONCE = 'Cipherstate has reached maximum n, a new handshake must be performed';
class AbstractHandshake {
    encryptWithAd(cs, ad, plaintext) {
        const e = this.encrypt(cs.k, cs.n, ad, plaintext);
        this.setNonce(cs, this.incrementNonce(cs.n));
        return e;
    }
    decryptWithAd(cs, ad, ciphertext) {
        const { plaintext, valid } = this.decrypt(cs.k, cs.n, ad, ciphertext);
        this.setNonce(cs, this.incrementNonce(cs.n));
        return { plaintext, valid };
    }
    // Cipher state related
    hasKey(cs) {
        return !this.isEmptyKey(cs.k);
    }
    setNonce(cs, nonce) {
        cs.n = nonce;
    }
    createEmptyKey() {
        return new Uint8Array(32);
    }
    isEmptyKey(k) {
        const emptyKey = this.createEmptyKey();
        return (0, equals_1.equals)(emptyKey, k);
    }
    incrementNonce(n) {
        return n + 1;
    }
    nonceToBytes(n) {
        // Even though we're treating the nonce as 8 bytes, RFC7539 specifies 12 bytes for a nonce.
        const nonce = new Uint8Array(12);
        new DataView(nonce.buffer, nonce.byteOffset, nonce.byteLength).setUint32(4, n, true);
        return nonce;
    }
    encrypt(k, n, ad, plaintext) {
        if (n > exports.MAX_NONCE) {
            throw new Error(ERR_MAX_NONCE);
        }
        const nonce = this.nonceToBytes(n);
        const ctx = new chacha20poly1305_1.ChaCha20Poly1305(k);
        return ctx.seal(nonce, plaintext, ad);
    }
    encryptAndHash(ss, plaintext) {
        let ciphertext;
        if (this.hasKey(ss.cs)) {
            ciphertext = this.encryptWithAd(ss.cs, ss.h, plaintext);
        }
        else {
            ciphertext = plaintext;
        }
        this.mixHash(ss, ciphertext);
        return ciphertext;
    }
    decrypt(k, n, ad, ciphertext) {
        if (n > exports.MAX_NONCE) {
            throw new Error(ERR_MAX_NONCE);
        }
        const nonce = this.nonceToBytes(n);
        const ctx = new chacha20poly1305_1.ChaCha20Poly1305(k);
        const encryptedMessage = ctx.open(nonce, ciphertext, ad);
        if (encryptedMessage) {
            return {
                plaintext: encryptedMessage,
                valid: true
            };
        }
        else {
            return {
                plaintext: new Uint8Array(0),
                valid: false
            };
        }
    }
    decryptAndHash(ss, ciphertext) {
        let plaintext;
        let valid = true;
        if (this.hasKey(ss.cs)) {
            ({ plaintext, valid } = this.decryptWithAd(ss.cs, ss.h, ciphertext));
        }
        else {
            plaintext = ciphertext;
        }
        this.mixHash(ss, ciphertext);
        return { plaintext, valid };
    }
    dh(privateKey, publicKey) {
        try {
            const derivedU8 = x25519.sharedKey(privateKey, publicKey);
            if (derivedU8.length === 32) {
                return derivedU8;
            }
            return derivedU8.slice(0, 32);
        }
        catch (e) {
            (0, logger_1.logger)(e.message);
            return new Uint8Array(32);
        }
    }
    mixHash(ss, data) {
        ss.h = this.getHash(ss.h, data);
    }
    getHash(a, b) {
        const u = SHA256.hash((0, concat_1.concat)([a, b], a.length + b.length));
        return u;
    }
    mixKey(ss, ikm) {
        const [ck, tempK] = (0, utils_1.getHkdf)(ss.ck, ikm);
        ss.cs = this.initializeKey(tempK);
        ss.ck = ck;
    }
    initializeKey(k) {
        const n = exports.MIN_NONCE;
        return { k, n };
    }
    // Symmetric state related
    initializeSymmetric(protocolName) {
        const protocolNameBytes = (0, uint8arrays_1.fromString)(protocolName, 'utf-8');
        const h = this.hashProtocolName(protocolNameBytes);
        const ck = h;
        const key = this.createEmptyKey();
        const cs = this.initializeKey(key);
        return { cs, ck, h };
    }
    hashProtocolName(protocolName) {
        if (protocolName.length <= 32) {
            const h = new Uint8Array(32);
            h.set(protocolName);
            return h;
        }
        else {
            return this.getHash(protocolName, new Uint8Array(0));
        }
    }
    split(ss) {
        const [tempk1, tempk2] = (0, utils_1.getHkdf)(ss.ck, new Uint8Array(0));
        const cs1 = this.initializeKey(tempk1);
        const cs2 = this.initializeKey(tempk2);
        return { cs1, cs2 };
    }
    writeMessageRegular(cs, payload) {
        const ciphertext = this.encryptWithAd(cs, new Uint8Array(0), payload);
        const ne = this.createEmptyKey();
        const ns = new Uint8Array(0);
        return { ne, ns, ciphertext };
    }
    readMessageRegular(cs, message) {
        return this.decryptWithAd(cs, new Uint8Array(0), message.ciphertext);
    }
}
exports.AbstractHandshake = AbstractHandshake;
//# sourceMappingURL=abstract-handshake.js.map